/**
 * Case Data Transformation Utilities
 *
 * This file contains functions to transform form data into API request format
 */

import { CallerInformationFormValues } from '@/validation/schemas/callerInformation'
import { AmbulanceRequestInformationFormValues } from '@/validation/schemas/ambulanceRequestInformation'
import { CreateCaseRequest } from '@/types/api/cases'

const coerceToDate = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

const calculateAgeYears = (
  dob: unknown,
  now: Date = new Date()
): number | null => {
  const birthDate = coerceToDate(dob)
  if (!birthDate) return null

  const nowY = now.getUTCFullYear()
  const nowM = now.getUTCMonth()
  const nowD = now.getUTCDate()
  const birthY = birthDate.getUTCFullYear()
  const birthM = birthDate.getUTCMonth()
  const birthD = birthDate.getUTCDate()

  let age = nowY - birthY
  if (nowM < birthM || (nowM === birthM && nowD < birthD)) age -= 1

  if (!Number.isFinite(age)) return null
  return Math.max(0, age)
}

/**
 * Transform caller information form data to API format
 */
export const transformCallerData = (
  callerData: CallerInformationFormValues,
  notes?: string
): Partial<CreateCaseRequest> => {
  return {
    call_type: callerData.call_category,
    caller_information: {
      prefix: callerData.prefix,
      first_name: callerData.first_name,
      last_name: callerData.last_name,
      phone_number: callerData.phone_number,
      gender: callerData.gender,
    },
    // Location from caller data - use Google Places data if available
    address_line_1: callerData.location,
    state: callerData.location_state || 'Lagos', // Use Google Places state or default
    country: 'Nigeria',
    // Use coordinates from Google Places if available, otherwise default to Lagos
    location_latitude: callerData.location_latitude || 6.5244,
    location_longitude: callerData.location_longitude || 3.3792,
    // Don't set is_patient_caller here - it will be set from ambulance form
    source: 'web-app',
    // Include note if provided (singular key for request)
    note: notes || undefined,
  }
}

/**
 * Transform ambulance request information form data to API format
 */
export const transformAmbulanceData = (
  ambulanceData: AmbulanceRequestInformationFormValues,
  notes?: string
): Partial<CreateCaseRequest> => {
  // Build notes: use provided notes or generate from pickup/destination info
  const locationNotes = `Pickup: ${ambulanceData.pickup_location}, ${ambulanceData.pickup_state}, ${ambulanceData.pickup_lga}\nDestination: ${ambulanceData.destination}, ${ambulanceData.destination_state}, ${ambulanceData.destination_lga}`
  const finalNotes = notes ? `${notes}\n\n${locationNotes}` : locationNotes

  return {
    // Patient information - array of patients
    patients_information: [
      {
        patient_first_name: ambulanceData.first_name,
        patient_last_name: ambulanceData.last_name,
        patient_dob: ambulanceData.date_of_birth
          ? ambulanceData.date_of_birth instanceof Date
            ? ambulanceData.date_of_birth.toISOString().split('T')[0]
            : ambulanceData.date_of_birth
          : '',
        patient_age: ambulanceData.age,
        patient_weight: ambulanceData.weight.toString(), // Convert to string as expected by backend
        patient_gender: ambulanceData.gender,
        // Optional fields that could be added to the form later
        patient_condition: ambulanceData.is_conscious,
        patient_allergies: undefined,
        patient_medication: undefined,
        patient_pre_existing_condition: undefined,
        patient_health_insurance_coverage: undefined,
        patient_phone_number: undefined,
        notify_emergency_contacts: undefined,
      },
    ],

    // Medical information
    medical_information: {
      is_patient_concious: ambulanceData.is_conscious === 'yes',
      oxygen_required: ambulanceData.oxygen_required === 'yes',
      severity: ambulanceData.severity_level,
      provisional_diagnosis: ambulanceData.provisional_diagnosis,
    },

    // Location information (pickup and destination)
    address_line_1: ambulanceData.pickup_location,
    address_line_2: ambulanceData.destination,
    state: ambulanceData.pickup_state,
    country: 'Nigeria',
    // Pickup coordinates from Google Maps (used as location_latitude/longitude in API)
    location_latitude: ambulanceData.pickup_location_latitude || 6.5244,
    location_longitude: ambulanceData.pickup_location_longitude || 3.3792,
    // Dropoff coordinates from Google Maps
    dropoff_location_latitude:
      ambulanceData.dropoff_location_latitude || 6.5244,
    dropoff_location_longitude:
      ambulanceData.dropoff_location_longitude || 3.3792,
    // Concatenated location strings
    pickup_location: `${ambulanceData.pickup_location}, ${ambulanceData.pickup_state}, ${ambulanceData.pickup_lga}`,
    dropoff_location: `${ambulanceData.destination}, ${ambulanceData.destination_state}, ${ambulanceData.destination_lga}`,

    // Patient caller flag from checkbox
    is_patient_caller: ambulanceData.same_as_caller,

    // Additional information (singular key for request)
    note: finalNotes,
    provisional_diagnosis: ambulanceData.provisional_diagnosis,
    probable_cause: ambulanceData.provisional_diagnosis,
  }
}

/**
 * Combine caller and ambulance data into a complete API request
 */
export const combineCaseData = (
  callerData: CallerInformationFormValues,
  ambulanceData: AmbulanceRequestInformationFormValues,
  notes?: string
): CreateCaseRequest => {
  const callerTransformed = transformCallerData(callerData, notes)
  const ambulanceTransformed = transformAmbulanceData(ambulanceData, notes)

  // Merge the data, with ambulance data taking precedence for overlapping fields
  const combined: CreateCaseRequest = {
    ...callerTransformed,
    ...ambulanceTransformed,

    // Ensure required fields are present
    address_line_1:
      ambulanceTransformed.address_line_1 ||
      callerTransformed.address_line_1 ||
      '',
    state: ambulanceTransformed.state || callerTransformed.state || 'Lagos',
    // Pickup coordinates - prefer ambulance form coordinates (from Google Maps on pickup field)
    // Fall back to caller coordinates if pickup wasn't changed
    location_latitude:
      ambulanceTransformed.location_latitude ||
      callerTransformed.location_latitude ||
      6.5244,
    location_longitude:
      ambulanceTransformed.location_longitude ||
      callerTransformed.location_longitude ||
      3.3792,
    // Dropoff coordinates from ambulance form
    dropoff_location_latitude:
      ambulanceTransformed.dropoff_location_latitude || 6.5244,
    dropoff_location_longitude:
      ambulanceTransformed.dropoff_location_longitude || 3.3792,
    pickup_location: ambulanceTransformed.pickup_location || '',
    dropoff_location: ambulanceTransformed.dropoff_location || '',
    call_type:
      callerTransformed.call_type || 'general-inquiry/service-information',
    caller_information: callerTransformed.caller_information!,
    source: 'web-app',
    // Use ambulance transformed note as it combines user notes with location info
    note: ambulanceTransformed.note,
  }

  // If "Same as caller's information" is checked, ensure the patient record includes phone number.
  // There is no patient phone number field in the AmbulanceRequestInformation form.
  if (
    ambulanceData.same_as_caller &&
    combined.is_patient_caller &&
    combined.caller_information?.phone_number &&
    Array.isArray(combined.patients_information) &&
    combined.patients_information.length > 0
  ) {
    const normalize = (value: any) =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    const callerFirst = normalize(combined.caller_information.first_name)
    const callerLast = normalize(combined.caller_information.last_name)
    const callerAge = calculateAgeYears(callerData?.date_of_birth)

    const matchIndex = combined.patients_information.findIndex(
      (patient: any) => {
        const patientFirst = normalize(patient?.patient_first_name)
        const patientLast = normalize(patient?.patient_last_name)
        return patientFirst === callerFirst && patientLast === callerLast
      }
    )

    const targetIndex = matchIndex >= 0 ? matchIndex : 0
    combined.patients_information = combined.patients_information.map(
      (patient: any, index: number) =>
        index === targetIndex
          ? {
              ...patient,
              patient_phone_number: combined.caller_information.phone_number,
              ...(typeof callerAge === 'number'
                ? { patient_age: callerAge }
                : {}),
            }
          : patient
    )
  }

  return combined
}

/**
 * Transform caller-only data (for non-ambulance requests)
 */
export const transformCallerOnlyData = (
  callerData: CallerInformationFormValues,
  notes?: string
): CreateCaseRequest => {
  const transformed = transformCallerData(callerData, notes)
  const caseOpenTime = new Date().toISOString()

  return {
    ...transformed,
    // Ensure required backend fields are present
    case_status: 'open',
    case_open_time_pk: generateCaseOpenTimePk(caseOpenTime),
    case_open_time: caseOpenTime,
    // Ensure required fields are present
    address_line_1: transformed.address_line_1 || '',
    state: transformed.state || 'Lagos',
    // Use coordinates from Google Places if available
    location_latitude: transformed.location_latitude || 6.5244,
    location_longitude: transformed.location_longitude || 3.3792,
    call_type: transformed.call_type || 'general-inquiry/service-information',
    caller_information: transformed.caller_information!,
    // For non-ambulance requests, the caller is not the patient
    is_patient_caller: false,
    source: 'web-app',
    // Include note if provided (singular key for request)
    note: notes || undefined,
  } as CreateCaseRequest
}

/**
 * Generate a unique case ID (if needed)
 */
export const generateCaseId = (): string => {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 8)
  return `CASE_${timestamp}_${random}`.toUpperCase()
}

/**
 * Generate case_open_time_pk based on case open time
 * This is used for DynamoDB indexing and querying
 */
export const generateCaseOpenTimePk = (caseOpenTime: string): string => {
  const date = new Date(caseOpenTime)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `case_open_time_pk#${year}-${month}-${day}`
}
